<script setup>
import InfoIcon from '@iconify-vue/ph/info-fill';
import { toString, truncate } from '@js/utils/string';
import { AnimatePresence, Motion } from 'motion-v';
import {
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipRoot,
    TooltipTrigger,
} from 'reka-ui';
import { computed, ref } from 'vue';

const {
    text,
    tooltipPlacement = 'right',
    tooltipIcon = InfoIcon,
    tooltipContent = null,
    tooltipColor = null,
    align = 'left',
    color = null,
    length = 50,
    lineThrough = false,
    bold = false,
    title = null,
    minWidth = null,
    maxWidth = null,
    displayAsNumber = false,
} = defineProps({
    text: {
        type: String,
        required: true,
    },
    tooltipPlacement: {
        type: String,
        required: false,
    },
    tooltipIcon: {
        required: false,
    },
    tooltipContent: {
        type: [String, Object],
        required: false,
    },
    tooltipColor: {
        type: String,
        required: false,
    },
    align: {
        type: String,
        required: false,
    },
    color: {
        type: String,
        required: false,
    },
    length: {
        type: Number,
        required: false,
    },
    lineThrough: {
        type: Boolean,
        required: false,
    },
    bold: {
        type: Boolean,
        required: false,
    },
    title: {
        type: String,
        required: false,
    },
    minWidth: {
        type: Number,
        required: false,
    },
    maxWidth: {
        type: Number,
        required: false,
    },
    displayAsNumber: {
        type: Boolean,
        required: false,
    },
});

const isTooltipOpen = ref(false);

const hasTooltipContent = computed(
    () =>
        tooltipContent !== null &&
        tooltipContent !== '' &&
        typeof tooltipContent !== 'undefined',
);
</script>

<template>
    <div
        class="relative inline-flex h-full items-center p-4"
        :class="{
            'justify-start': align === 'left',
            'justify-center text-center': align === 'center',
            'justify-end': align === 'right',
            'text-lg font-bold': displayAsNumber,
        }"
        :style="{
            minWidth: `${minWidth}px`,
            maxWidth: `${maxWidth}px`,
        }"
    >
        <div
            :class="{
                'line-through': lineThrough,
                'font-bold': bold,
                'text-red-500': color === 'red',
                'text-green-500': color === 'green',
            }"
            :title="title"
            v-html="truncate(toString(text), length)"
        ></div>

        <div
            v-if="hasTooltipContent"
            class="relative flex items-center justify-center pl-1"
        >
            <TooltipProvider>
                <TooltipRoot v-model:open="isTooltipOpen" :delay-duration="0">
                    <TooltipTrigger as-child>
                        <button type="button">
                            <component
                                :is="tooltipIcon"
                                class="size-5 cursor-help"
                                :class="{
                                    'text-indigo-600': tooltipColor === null,
                                    'text-red-500': tooltipColor === 'red',
                                    'text-orange-500':
                                        tooltipColor === 'orange',
                                    'text-yellow-500':
                                        tooltipColor === 'yellow',
                                    'text-green-500': tooltipColor === 'green',
                                }"
                            />
                        </button>
                    </TooltipTrigger>

                    <TooltipPortal>
                        <AnimatePresence>
                            <TooltipContent
                                v-if="isTooltipOpen"
                                as-child
                                force-mount
                                :side="tooltipPlacement"
                                :side-offset="5"
                            >
                                <Motion
                                    :initial="{ opacity: 0, scale: 0.96 }"
                                    :animate="{ opacity: 1, scale: 1 }"
                                    :exit="{ opacity: 0, scale: 0.96 }"
                                    :transition="{ duration: 0.15 }"
                                    class="wysiwyg z-50 max-h-32 w-96 overflow-y-auto rounded bg-white px-2 py-1 drop-shadow-sm"
                                >
                                    <div
                                        v-if="
                                            typeof tooltipContent === 'string'
                                        "
                                        v-html="tooltipContent"
                                    ></div>
                                    <component v-else :is="tooltipContent" />
                                </Motion>
                            </TooltipContent>
                        </AnimatePresence>
                    </TooltipPortal>
                </TooltipRoot>
            </TooltipProvider>
        </div>

        <div v-if="$slots.actions" class="ml-2 flex gap-2">
            <slot name="actions" />
        </div>
    </div>
</template>
